import React, { useEffect, useState } from 'react';
import { getCurrentUser, AuthUser } from 'aws-amplify/auth';
import { GET_DAILY_RECORD_BY_MONTH } from '../../../graphql/queries';
import client from '../../../graphql/apolloClient';
import SignInPage from '../../Authentication/SignInPage';
import DefaultLayout from '../../../layout/DefaultLayout';
import LoadingIndicator from '../../../components/Loading/LoadingIndicator';
import AttendanceMonthTable from './AttendanceMonthTable';
import { AttendanceRecord } from '../../../common/Interface/AttendanceRecord';
import { calculateWorkingHour, timeDisplayFormat } from '../../../utils/Utils';

const AttendancePage: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [totalShiftTime, setTotalShiftTime] = useState<string>('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Error Fetching User', error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (user && selectedMonth && selectedYear) {
        const year = parseInt(selectedYear);
        const month = parseInt(selectedMonth);
        // 指定された年と月を使ってDateオブジェクトを構築し、月初めの午前0時（UTC+09:00）に設定する
        const firstDayOfMonth = new Date(
          `${year}-${month.toString().padStart(2, '0')}-01T00:00:00+09:00`,
        ); // データベースに保存されているUTCフォーマットへの時刻変更
        try {
          setDataLoading(true);
          const { data } = await client.query({
            query: GET_DAILY_RECORD_BY_MONTH,
            variables: { params: { UserID: user.userId, Date: firstDayOfMonth.toISOString() } },
            fetchPolicy: 'network-only', // キャッシュを使用せず、常に新しいデータを取得
          });
          if (data) {
            // ダミーデータとマージするためにデータベースからデータを取得。
            const attendanceDataOfMonth = mergeDummyWithActualData(
              data.getDailyRecordsByMonth || [],
              firstDayOfMonth,
              user.userId,
            );
            setAttendanceData(attendanceDataOfMonth);
          } else {
            console.log('No data Present');
            setAttendanceData([]);
          }
        } catch (error) {
          // その日に初めてページにアクセスされたときのみ発生する
          console.log('Error fetching data:', error);
          setAttendanceData([]);
        } finally {
          setDataLoading(false);
          setInitialLoading(false);
        }
      }
    };
    // This component is mounted, when the intial render is ran once
    fetchData();
  }, [user, refresh, selectedMonth, selectedYear]);

  useEffect(() => {
    // 初期ローディングが完了し、ローディングウィンドウのアクションが表示されないように値がfalseに設定される
    if (!dataLoading) {
      setInitialLoading(false);
      setRefresh(false);
    }
  }, [dataLoading]);

  // 勤怠データが変更された際に自動で合計時間を再計算する
  // attendanceDataが更新されるたびに（レコード編集、月変更、データフェッチ後など）
  // 画面に表示される合計時間を最新の状態に保つ必要があるため
  useEffect(() => {
    setTotalShiftTime(timeDisplayFormat(calculateTotalShiftTime()));
  }, [attendanceData]);

  /**
   * 勤怠データの変更を処理し、合計時間を更新する関数
   *
   * @param updatedRecord - サーバー同期後に返された更新済みレコード（オプショナル）
   *
   * この関数は2つのシナリオで呼び出される：
   * 1. 子コンポーネントでの編集→サーバー保存成功時（updatedRecordあり）
   * 2. その他の理由による合計時間再計算要求時（updatedRecordなし）
   */
  const validDataChange = (updatedRecord?: AttendanceRecord) => {
    if (updatedRecord) {
      // シナリオ1: サーバー同期成功時の処理
      // サーバーに保存されたデータが正確な状態なので、ローカルのattendanceData配列を
      // サーバーから返された最新データで更新する必要がある
      // これにより、データの不整合を防ぎ、UIとサーバーの状態を同期させる
      setAttendanceData((prevData) =>
        prevData.map(
          (record) =>
            // 日付とユーザーIDで該当レコードを特定
            record.Date.getTime() === updatedRecord.Date.getTime() &&
            record.UserID === updatedRecord.UserID
              ? { ...updatedRecord } // 該当レコードを新しいデータで置き換え
              : record, // その他のレコードはそのまま維持
        ),
      );
      // NOTE: attendanceDataが更新されると、useEffect(() => {...}, [attendanceData])により
      // 自動的に合計時間が再計算される
    } else {
      // シナリオ2: 手動再計算時の処理
      // attendanceDataの更新を経由せずに直接合計時間を更新したい場合
      // （例：データ整合性チェック、エラー回復、管理者による手動更新要求など）
      // useEffectによる自動更新を待たずに即座に合計時間を更新する
      const newTotalShiftTime = timeDisplayFormat(calculateTotalShiftTime());
      setTotalShiftTime(newTotalShiftTime);
    }
  };

  /**
   * attendanceData配列を使用して総シフト時間を計算する
   *
   * 各日の勤務時間を合計することで、月全体の総労働時間を算出する
   * - StartTimeとEndTimeが両方存在する日のみを対象とする
   * - 負の値（異常データ）は除外して正確な合計時間を保つ
   *
   * @returns 総シフト時間（分単位）
   */
  const calculateTotalShiftTime = () => {
    let totalShiftTime = 0;
    attendanceData.forEach((record) => {
      // 開始時刻と終了時刻が両方存在する場合のみ計算対象とする
      // どちらか一方でも欠けている場合、正確な勤務時間を算出できないため
      if (record.StartTime && record.EndTime) {
        const workingTime = calculateWorkingHour(record.StartTime, record.EndTime);

        // 合計時間計算式: シフト時間が正の場合のみ加算する
        // 負の値は異常データ（終了時刻が開始時刻より早いなど）なので除外
        if (workingTime > 0) {
          totalShiftTime += workingTime;
        }
      }
    });
    return totalShiftTime;
  };

  /**
   * サーバーから取得したデータとダミーデータをマージして完全な月のデータを作成する
   *
   * データベースには実際に勤怠データが存在する日のみが保存されているため、
   * 月表示では全ての日（1日～月末）を表示する必要がある
   * 存在しない日はnullのダミーデータで埋めることで、一貫したUIを提供する
   *
   * @param fetchRecords - サーバーから取得した実際の勤怠データ
   * @param firstDayOfMonth - 対象月の1日
   * @param userId - ユーザーID
   * @returns 月全体の勤怠データ配列（実データ + ダミーデータ）
   */
  const mergeDummyWithActualData = (
    fetchRecords: AttendanceRecord[],
    firstDayOfMonth: Date,
    userId: string,
  ): AttendanceRecord[] => {
    const fillRecords: AttendanceRecord[] = [];

    // 対象月の最終日を計算
    // 例: Sun Mar 31 2024 00:00:00 GMT+0900 (Japan Standard Time)
    const lastElementOfMonth = new Date(
      firstDayOfMonth.getFullYear(),
      firstDayOfMonth.getMonth() + 1,
      0,
    );

    // 月の全日分のダミーデータを準備する
    // 月表示では1日から月末までのすべての日を表示する必要があるため
    let dummyDay = firstDayOfMonth;
    while (dummyDay <= lastElementOfMonth) {
      // nullデータでのダミーレコードを作成（UTCフォーマット）
      const newRecord: AttendanceRecord = {
        __typename: 'DailyRecord',
        UserID: userId,
        Date: dummyDay,
        Leave: null,
        StartTime: null,
        EndTime: null,
        Note: null,
      };
      fillRecords.push(newRecord);

      // 翌日に進める
      dummyDay = new Date(dummyDay);
      dummyDay.setDate(dummyDay.getDate() + 1);
    }

    // ダミーデータと実データをマージ
    fillRecords.forEach((fillRecord, index) => {
      // 現在のダミーレコードの日付と一致する実データを検索
      const matchingfetchRecord = fetchRecords.find(
        (fetchRecord) => new Date(fetchRecord.Date).getTime() === fillRecord.Date.getTime(),
      );

      if (matchingfetchRecord) {
        // 実データが存在する場合：ダミーデータを実データで置き換え
        // データベースから取得した正確なデータを表示するため
        fillRecords[index] = {
          ...matchingfetchRecord,
          Date: new Date(matchingfetchRecord.Date),
          StartTime: matchingfetchRecord.StartTime ? new Date(matchingfetchRecord.StartTime) : null,
          EndTime: matchingfetchRecord.EndTime ? new Date(matchingfetchRecord.EndTime) : null,
        };
      } else {
        // 実データが存在しない場合：ダミーデータ（null値）のまま維持
        // 勤怠データが登録されていない日は空欄で表示するため
        //console.log('Record not matched.');
      }
    });
    return fillRecords;
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault(); // Prevent default form submission behavior
    setSelectedMonth(event.target.value); // Update selected month when user selects a different month
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault(); // Prevent default form submission behavior
    setSelectedYear(event.target.value);
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear().toString();
    const currenMonth = (new Date().getMonth() + 1).toString();
    setSelectedYear(currentYear);
    setSelectedMonth(currenMonth);
  }, []);

  if (initialLoading || dataLoading) {
    // Show loading indicator during intial loading
    return (
      <DefaultLayout>
        <LoadingIndicator />
      </DefaultLayout>
    );
  }

  if (user !== null) {
    return (
      <DefaultLayout>
        <div className="p-6 bg-opacity-75 bg-blue-100 backdrop-blur-lg rounded-md">
          <div className="border bg-blue-900 rounded-lg p-4 mb-4">
            <div className="relative mt-2 flex gap-4">
              <select
                value={selectedYear || ''}
                onChange={handleYearChange}
                className="block w-32 rounded-md border-0 py-2.5 pl-4 pr-8 text-black ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 text-sm leading-6 bg-white shadow-sm"
              >
                <option value="">選択年</option>
                {Array.from({ length: 5 }, (_, index) => {
                  const year = new Date().getFullYear() - index;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}年
                    </option>
                  );
                })}
              </select>

              <select
                value={selectedMonth || ''}
                onChange={handleMonthChange}
                className="block w-32 rounded-md border-0 py-2.5 pl-4 pr-8 text-black ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 text-sm leading-6 bg-white shadow-sm"
              >
                <option value="">月選択</option>
                {Array.from({ length: 12 }, (_, index) => {
                  const monthNumber = index + 1;
                  return (
                    <option key={monthNumber} value={monthNumber.toString()}>
                      {monthNumber}月
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          {dataLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <AttendanceMonthTable
                attendanceData={attendanceData}
                onValidDataChange={validDataChange}
              />
              <p className="mt-4 text-lg font-semibold text-black">合計時間: {totalShiftTime}</p>
            </>
          )}
        </div>
      </DefaultLayout>
    );
  } else {
    return <SignInPage />;
  }
};

export default AttendancePage;
